import type { Object3D } from 'three';
import type { ImportedModelInstancedUnitSource } from '../../../../types';
import type { ImportedModelInstancedMeshUnit } from '../mesh-unit';
import type { ImportedModelInstancedSelectionBatchManager } from './ImportedModelInstancedSelectionBatchManager';

/**
 * 消费正式 Unit 成员事件，在宿主空闲帧逐个预热完整选择批次。
 * 不模拟业务选择、不调用立即渲染、不创建 RAF/定时器；Unit 撤销和状态销毁都可取消任务。
 */
export class ImportedModelSelectionPrewarmer {
    /** 队列只持有尚未预热的 Unit；值用于提前销毁时解除排队。 */
    private readonly pending = new Map<ImportedModelInstancedMeshUnit, () => void>();
    /** 正式成员来源的退订入口。 */
    private unsubscribe: (() => void) | null = null;
    /** 当前帧显示恢复函数，绝不跨帧保留临时可见状态。 */
    private restoreVisibility: (() => void) | null = null;
    /** 销毁后不能被本帧 finally 再次排队。 */
    private disposed = false;
    /** 稳定任务对象；新 Unit 只更新队列，避免每次通知创建任务闭包。 */
    private readonly task: ViewerIdleRenderTask = {
        prepare: () => this.prepareNext(),
        finish: () => {
            try { this.restoreVisibility?.(); }
            finally {
                this.restoreVisibility = null;
                this.scheduleNext();
            }
        },
    };

    /**
     * @param source 当前 Viewer 的正式 Unit 成员来源。
     * @param scheduler 已存在的单一帧调度器。
     * @param batches 本视觉状态的稳定选择缓存。
     */
    public constructor(
        source: ImportedModelInstancedUnitSource,
        private readonly scheduler: ViewerIdleRenderSchedulerPort,
        private readonly batches: ImportedModelInstancedSelectionBatchManager,
    ) {
        try {
            this.unsubscribe = source.observeUnits((unit, present) => {
                if (present) this.enqueue(unit);
                else {
                    this.pending.get(unit)?.();
                    this.pending.delete(unit);
                    this.batches.removeUnit(unit);
                    if (this.pending.size === 0) this.scheduler.cancelIdleRender(this);
                }
            });
        } catch (error) {
            this.dispose();
            throw error;
        }
    }

    /** 注销队列、Unit 监听及帧任务；已预热缓存仍由批次管理器按既有生命周期释放。 */
    public dispose(): void {
        if (this.disposed) return;
        this.disposed = true;
        this.unsubscribe?.();
        this.unsubscribe = null;
        this.scheduler.cancelIdleRender(this);
        for (const unsubscribe of this.pending.values()) unsubscribe();
        this.pending.clear();
        this.restoreVisibility?.();
        this.restoreVisibility = null;
    }

    /** @param unit 新发布的正式 Unit；只登记，不在加载发布栈中创建 GPU 资源。 */
    private enqueue(unit: ImportedModelInstancedMeshUnit): void {
        if (this.disposed || this.pending.has(unit)) return;
        this.pending.set(unit, unit.onDispose(() => {
            this.pending.delete(unit);
            if (this.pending.size === 0) this.scheduler.cancelIdleRender(this);
        }));
        this.scheduleNext();
    }

    /** 每次只把一项交给空闲通道；持续鼠标交互期间不会强行插入预热帧。 */
    private scheduleNext(): void {
        if (!this.disposed && this.pending.size > 0) this.scheduler.scheduleIdleRender(this, this.task);
    }

    /**
     * 同步准备一项，真实管线完成后由 task.finish 恢复。
     * 隐藏或尚未挂载的 Unit 不强行显示其祖先；这类对象仍保留正常首次选择的惰性路径。
     */
    private prepareNext(): void {
        const entry = this.pending.entries().next().value;
        if (!entry || this.disposed) return;
        const [unit, unsubscribe] = entry;
        this.pending.delete(unit);
        unsubscribe();
        if (!unit.object.parent) return;
        for (let parent: Object3D | null = unit.object.parent; parent; parent = parent.parent) {
            if (!parent.visible) return;
        }
        this.restoreVisibility = this.batches.prepareWarmup(unit);
    }
}
